/**
 * Адрес комнаты, который роутер CRM «держит», пока идёт звонок.
 *
 * Зачем. Комната живёт только на своей странице, и роутер (wouter) снимает её,
 * как только адрес в строке браузера сменился. Спросить «Выйти из интервью?»
 * по событию popstate мало: Chrome сначала меняет адрес и только потом шлёт
 * событие, а React успевает перерисоваться в этом промежутке — замер
 * 28.09.2026: комната размонтировалась раньше, чем событие дошло до первого
 * слушателя. Поэтому, пока звонок идёт, роутер видит адрес комнаты независимо
 * от строки браузера, и комнату снимает только подтверждённый уход.
 *
 * Хозяин удержания — комната (useLiveCallGuard). Роутер CRM читает его через
 * useGuardedLocation. Без удержания роутер работает ровно как раньше.
 */

export interface LiveCallHold {
  /** Путь комнаты: его роутер видит всё время звонка. */
  path: string;
  /** Полный адрес комнаты — на него возвращаем строку браузера после «Остаться». */
  href: string;
  /** Что спросить перед уходом сейчас; null — спрашивать не о чем. */
  confirmText: () => string | null;
}

let current: (LiveCallHold & { id: number }) | null = null;
let nextId = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of [...listeners]) listener();
}

/** Начать удерживать адрес комнаты. Возвращает номер удержания для снятия. */
export function holdLiveCallLocation(hold: LiveCallHold): number {
  nextId += 1;
  current = { ...hold, id: nextId };
  notify();
  return nextId;
}

/** Снять своё удержание. Чужое (другой комнаты) не трогаем. */
export function releaseLiveCallLocation(id: number): void {
  if (current?.id !== id) return;
  current = null;
  notify();
}

/** Держит ли адрес именно это удержание. */
export function isLiveCallHeld(id: number): boolean {
  return current?.id === id;
}

/** Путь, который видит роутер вместо строки браузера; null — удержания нет. */
export function liveCallHeldPath(): string | null {
  return current?.path ?? null;
}

export function subscribeLiveCallLocation(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Спросить перед уходом со страницы звонка по ссылке. true — уходим: удержание
 * снято, и следующий переход роутер выполнит как обычно. false — остаёмся.
 * Без звонка спрашивать не о чем.
 */
export function confirmLeaveLiveCall(
  confirm: (text: string) => boolean
): boolean {
  const hold = current;
  if (!hold) return true;
  const text = hold.confirmText();
  if (text && !confirm(text)) return false;
  releaseLiveCallLocation(hold.id);
  return true;
}

/**
 * Переход роутера (ссылка, кнопка меню, navigate) во время звонка. true —
 * переходить.
 *
 * Спрашиваем ДО смены адреса, а не по событию pushState после неё: иначе
 * «Остаться» оставляло в истории вторую запись с адресом комнаты, и после
 * встречи первое «Назад» вело на ту же комнату (прогон 28.09.2026). На тот же
 * путь (сменились только параметры) — без вопроса: комнату это не снимает.
 */
export function allowLiveCallNavigation(
  to: string | URL,
  currentHref: string,
  confirm: (text: string) => boolean
): boolean {
  const held = current;
  if (!held) return true;
  if (new URL(String(to), currentHref).pathname === held.path) return true;
  return confirmLeaveLiveCall(confirm);
}
